import React, { useEffect, useState } from 'react';
import { Splide } from '@splidejs/react-splide';

interface Props {
  containerClasses?: string;
  buttonClasses?: string;
  splideRef: React.RefObject<any>;
}

const CarouselArrows: React.FC<Props> = ({
  containerClasses = 'flex items-center gap-x-2',
  buttonClasses = 'text-[16px]',
  splideRef
}) => {
  const [canGoPrev, setCanGoPrev] = useState(false);
  const [canGoNext, setCanGoNext] = useState(false);
  const [shouldShowArrows, setShouldShowArrows] = useState(true);

  const handleClickNext = () => {
    if (!splideRef.current) return;

    // Handle ObviyoProductRecommendations ref (has navigateToNext method)
    if (splideRef.current.navigateToNext) {
      splideRef.current.navigateToNext();
    }
    // Handle fallback Splide ref (has splide property)
    else if (splideRef.current.splide) {
      splideRef.current.splide.go('>');
    }
  };

  const handleClickPrev = () => {
    if (!splideRef.current) return;

    // Handle ObviyoProductRecommendations ref (has navigateToPrev method)
    if (splideRef.current.navigateToPrev) {
      splideRef.current.navigateToPrev();
    }
    // Handle fallback Splide ref (has splide property)
    else if (splideRef.current.splide) {
      splideRef.current.splide.go('<');
    }
  };

  const updateArrowStates = () => {
    // Get splide instance from either ref pattern
    let splide: any = null;
    if (splideRef.current?.splide) {
      // Handle both ObviyoProductRecommendations (splideRef.current.splide) and fallback (splideRef.current.splide)
      splide = splideRef.current.splide;
    }

    if (!splide) return;

    const currentPerPage = splide.options.perPage || 1;
    const newCanGoPrev = splide.index > 0;
    const newCanGoNext = splide.index + currentPerPage < splide.length;

    // Check if we should show arrows based on slide count
    const newShouldShowArrows = splide.length > currentPerPage;

    setCanGoPrev(newCanGoPrev);
    setCanGoNext(newCanGoNext);
    setShouldShowArrows(newShouldShowArrows);
  };

  useEffect(() => {
    // Get splide instance from either ref pattern
    let splide: any = null;
    if (splideRef.current?.splide) {
      splide = splideRef.current.splide;
    }

    if (!splide) return;

    // Listen for slide changes to update arrow states
    splide.on('moved', updateArrowStates);
    splide.on('mounted', updateArrowStates);

    // Initial state update
    updateArrowStates();

    return () => {
      splide.off('moved', updateArrowStates);
      splide.off('mounted', updateArrowStates);
    };
  }, [splideRef.current]); // Watch for changes in the ref content

  // Check if we should disable buttons based on slide type and rewind settings
  const getSplideInstance = () => splideRef.current?.splide;
  const splideInstance = getSplideInstance();

  const shouldDisablePrev =
    splideInstance?.options.type === 'slide' && !splideInstance?.options.rewind && !canGoPrev;

  const shouldDisableNext =
    splideInstance?.options.type === 'slide' && !splideInstance?.options.rewind && !canGoNext;

  // Don't render arrows if there aren't enough slides
  if (!shouldShowArrows) return null;

  return (
    <div className={`${containerClasses}`}>
      <button
        className={`${buttonClasses} disabled:opacity-50`}
        onClick={handleClickPrev}
        disabled={shouldDisablePrev}
        aria-label='Previous Slide'
      >
        <i className='icon-chevron-left'></i>
      </button>
      <button
        className={`${buttonClasses} disabled:opacity-50`}
        onClick={handleClickNext}
        disabled={shouldDisableNext}
        aria-label='Next Slide'
      >
        <i className='icon-chevron-right'></i>
      </button>
    </div>
  );
};

export default CarouselArrows;
